// Draws a guided tour (tour.ts): a spotlight on the step's element and a callout
// beside it. It is one of the app's dialogs (`[data-modal]`, dialog.ts), so Esc
// closes it, Enter goes on (or presses the focused button), and a click outside
// ends it (nothing to lose); ← / → step back and forth.

import { tr } from './i18n';
import {
  type TourId,
  TOURS,
} from './tour';

const CARD_W = 320;
const GAP = 12;

let open: HTMLElement | null = null;

function visibleRect(sel: string): DOMRect | null {
  const el = document.querySelector<HTMLElement>(sel);
  if (!el) return null;
  const r = el.getBoundingClientRect();
  return r.width > 0 && r.height > 0 ? r : null;
}

export function runTour(id: TourId): void {
  open?.remove();
  const tour = TOURS[id];
  const root = document.createElement('div');
  root.className = 'tour';
  root.dataset.modal = '';
  root.dataset.dismissable = '';
  root.dataset.tour = id;
  const spot = document.createElement('div');
  spot.className = 'tour-spot';
  const card = document.createElement('div');
  card.className = 'tour-card';
  card.setAttribute('role', 'dialog');
  const count = document.createElement('div'); count.className = 'tour-count';
  const title = document.createElement('h3');
  const body = document.createElement('p'); body.className = 'tour-body';
  const actions = document.createElement('div'); actions.className = 'modal-actions';
  const close = document.createElement('button'); close.className = 'ghost'; close.dataset.role = 'cancel'; close.textContent = tr('Close');
  const back = document.createElement('button'); back.className = 'ghost tour-back'; back.textContent = tr('Back');
  const next = document.createElement('button'); next.className = 'primary tour-next'; next.dataset.role = 'confirm';
  actions.append(close, back, next);
  card.append(count, title, body, actions);
  root.append(spot, card);
  document.body.appendChild(root);
  open = root;

  let step = 0;
  const end = (): void => {
    root.remove();
    window.removeEventListener('resize', place);
    window.removeEventListener('keydown', keys, true);
    if (open === root) open = null;
  };
  function place(): void {
    const s = tour.steps[step];
    count.textContent = `${tr(tour.title)} · ${tr('Step {n} of {total}').replace('{n}', String(step + 1)).replace('{total}', String(tour.steps.length))}`;
    title.textContent = tr(s.title);
    body.textContent = tr(s.body);
    back.disabled = step === 0;
    next.textContent = tr(step === tour.steps.length - 1 ? 'Done' : 'Next');
    root.dataset.step = String(step);
    const r = visibleRect(s.target);
    const vw = window.innerWidth, vh = window.innerHeight;
    spot.classList.toggle('hidden', !r);
    const cardH = card.offsetHeight || 180;
    let x = (vw - CARD_W) / 2, y = (vh - cardH) / 2;
    if (r) {
      Object.assign(spot.style, { left: `${r.left - 4}px`, top: `${r.top - 4}px`, width: `${r.width + 8}px`, height: `${r.height + 8}px` });
      x = r.left + r.width / 2 - CARD_W / 2;
      if (r.height > vh / 2) y = r.top + r.height / 2 - cardH / 2; // a big target: the card sits on it
      else if (r.bottom + GAP + cardH <= vh) y = r.bottom + GAP;
      else y = r.top - GAP - cardH;
    }
    card.style.left = `${Math.max(GAP, Math.min(vw - CARD_W - GAP, x))}px`;
    card.style.top = `${Math.max(GAP, Math.min(vh - cardH - GAP, y))}px`;
  }
  const go = (d: number): void => {
    const n = step + d;
    if (n < 0) return;
    if (n >= tour.steps.length) { end(); return; }
    step = n;
    place();
    next.focus();
  };
  close.addEventListener('click', end);
  back.addEventListener('click', () => go(-1));
  next.addEventListener('click', () => go(1));
  // The tour owns the keyboard while it is up: nothing reaches the drawing (an
  // arrow would nudge the selection). Esc is dialog.ts's, which runs first.
  function keys(e: KeyboardEvent): void {
    if (!root.isConnected) return;
    e.stopPropagation();
    const onButton = (e.target as HTMLElement | null)?.tagName === 'BUTTON' && root.contains(e.target as Node);
    if (e.key === 'ArrowRight' || (e.key === 'Enter' && !onButton)) { e.preventDefault(); go(1); }
    else if (e.key === 'ArrowLeft') { e.preventDefault(); go(-1); }
  }
  window.addEventListener('keydown', keys, true);
  window.addEventListener('resize', place);
  place();
  requestAnimationFrame(place); // the card's real height, now it is laid out
  setTimeout(() => next.focus(), 0);
}
